/**
 * FAQList Component
 *
 * Displays a grid of FAQ cards with ratings, approval status, and actions
 * Extracted from FAQs.tsx as part of Phase 3 decomposition
 *
 * Responsibilities:
 * - Display FAQ cards in a responsive grid
 * - Handle loading state with skeletons
 * - Handle empty state
 * - FAQ rating (helpful/not helpful)
 * - FAQ actions (view details, edit)
 * - Approval workflow integration
 */

import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  CardActions,
  Button,
  Skeleton,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  HelpOutline as HelpIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

import { FAQDocumentResponse, DocumentVisibility, ApprovalStatus } from '../../api/data-contracts';
import { ApprovalStatusBadge } from './ApprovalStatusBadge';
import { ApprovalActions } from './ApprovalActions';

export interface FAQListProps {
  /** Array of FAQ documents to display */
  faqs: FAQDocumentResponse[];
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Whether current user is a moderator */
  isModerator: boolean;
  /** Current user object */
  user: { id: string } | null;
  /** Callback when user rates an FAQ */
  onRateFAQ: (faqId: string, helpful: boolean) => void;
  /** Callback when user removes their vote from an FAQ */
  onUnrateFAQ: (faqId: string) => void;
  /** Callback when user clicks to view FAQ details */
  onViewDetails: (faq: FAQDocumentResponse) => void;
  /** Callback when user clicks to edit FAQ */
  onEditFAQ: (faq: FAQDocumentResponse) => void;
  /** Callback when user clicks to delete FAQ */
  onDeleteFAQ?: (faq: FAQDocumentResponse) => void;
  /** Function to get visibility icon */
  getVisibilityIcon: (visibility: DocumentVisibility) => JSX.Element;
  /** Function to get visibility text */
  getVisibilityText: (faq: FAQDocumentResponse) => string;
  /** Callback to show success message */
  showSuccess: (message: string) => void;
  /** Callback to show error message */
  showError: (message: string) => void;
}

/**
 * Memoized FAQ Card Component
 * Only re-renders when its specific FAQ changes
 */
const FAQCard = React.memo<{
  faq: FAQDocumentResponse;
  isModerator: boolean;
  user: { id: string } | null;
  onRateFAQ: (faqId: string, helpful: boolean) => void;
  onUnrateFAQ: (faqId: string) => void;
  onViewDetails: (faq: FAQDocumentResponse) => void;
  onEditFAQ: (faq: FAQDocumentResponse) => void;
  onDeleteFAQ?: (faq: FAQDocumentResponse) => void;
  getVisibilityIcon: (visibility: DocumentVisibility) => JSX.Element;
  getVisibilityText: (faq: FAQDocumentResponse) => string;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}>(({
  faq,
  isModerator,
  user,
  onRateFAQ,
  onUnrateFAQ,
  onViewDetails,
  onEditFAQ,
  onDeleteFAQ,
  getVisibilityIcon,
  getVisibilityText,
  showSuccess,
  showError
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Simplified markdown components for list preview
  const markdownComponents = {
    p: ({ ...props }) => (
      <Typography
        variant="body2"
        component="p"
        color="text.secondary"
        sx={{ mb: 1, lineHeight: 1.6 }}
        {...props}
      />
    ),
    h1: ({ ...props }) => (
      <Typography
        variant="subtitle1"
        component="h1"
        color="text.secondary"
        sx={{ mt: 0.5, mb: 0.5, fontWeight: 600 }}
        {...props}
      />
    ),
    h2: ({ ...props }) => (
      <Typography
        variant="subtitle2"
        component="h2"
        color="text.secondary"
        sx={{ mt: 0.5, mb: 0.5, fontWeight: 600 }}
        {...props}
      />
    ),
    h3: ({ ...props }) => (
      <Typography
        variant="body2"
        component="h3"
        color="text.secondary"
        sx={{ mt: 0.5, mb: 0.5, fontWeight: 600 }}
        {...props}
      />
    ),
    ul: ({ ...props }) => (
      <Box
        component="ul"
        sx={{ pl: 2, my: 0.5, color: 'text.secondary', '& li': { mb: 0.25 } }}
        {...props}
      />
    ),
    ol: ({ ...props }) => (
      <Box
        component="ol"
        sx={{ pl: 2, my: 0.5, color: 'text.secondary', '& li': { mb: 0.25 } }}
        {...props}
      />
    ),
    li: ({ ...props }) => (
      <Typography
        component="li"
        variant="body2"
        color="text.secondary"
        sx={{ lineHeight: 1.6 }}
        {...props}
      />
    ),
    code: ({ ...props }) => (
      <Box
        component="code"
        sx={{
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.dark,
          padding: '2px 4px',
          borderRadius: 0.5,
          fontSize: '0.85em',
          fontFamily: 'monospace',
        }}
        {...props}
      />
    ),
    strong: ({ ...props }) => (
      <Box
        component="strong"
        sx={{ fontWeight: 700, color: 'text.primary' }}
        {...props}
      />
    ),
    em: ({ ...props }) => (
      <Box
        component="em"
        sx={{ fontStyle: 'italic' }}
        {...props}
      />
    ),
  };

  const handleThumbsUp = useCallback(() => {
    // Toggle: if already voted helpful, remove the vote
    if (faq.user_vote === 'helpful') {
      onUnrateFAQ(faq.document_id);
    } else {
      onRateFAQ(faq.document_id, true);
    }
  }, [faq.document_id, faq.user_vote, onRateFAQ, onUnrateFAQ]);

  const handleThumbsDown = useCallback(() => {
    // Toggle: if already voted not helpful, remove the vote
    if (faq.user_vote === 'not_helpful') {
      onUnrateFAQ(faq.document_id);
    } else {
      onRateFAQ(faq.document_id, false);
    }
  }, [faq.document_id, faq.user_vote, onRateFAQ, onUnrateFAQ]);

  const handleViewDetails = useCallback(() => {
    onViewDetails(faq);
  }, [faq, onViewDetails]);

  const handleEdit = useCallback(() => {
    onEditFAQ(faq);
  }, [faq, onEditFAQ]);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        {/* FAQ Question */}
        <Typography variant="h6" component="h3" sx={{ mb: 2, lineHeight: 1.3 }}>
          {faq.question}
        </Typography>

        {/* FAQ Answer Preview with Markdown */}
        <Box
          sx={{
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            '& > *:first-of-type': { mt: 0 },
            '& > *:last-child': { mb: 0 },
          }}
        >
          <ReactMarkdown components={markdownComponents}>
            {faq.answer}
          </ReactMarkdown>
        </Box>

        {/* Categories */}
        {faq.category_tags && faq.category_tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {faq.category_tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
          </Box>
        )}

        {/* Metadata */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
          {/* Approval Status */}
          <ApprovalStatusBadge status={faq.approval_status || ApprovalStatus.Draft} size="small" />

          {/* Visibility */}
          <Tooltip title={getVisibilityText(faq)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getVisibilityIcon(faq.visibility || DocumentVisibility.Organization)}
            </Box>
          </Tooltip>
        </Box>
      </CardContent>

      <Divider />

      <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
        {/* Rating Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={handleThumbsUp}
            aria-label={t('faq.actions.helpful')}
            aria-pressed={faq.user_vote === 'helpful'}
            sx={{
              color: faq.user_vote === 'helpful' ? 'success.main' : 'action.active',
              bgcolor: faq.user_vote === 'helpful' ? 'success.main' : 'transparent',
              border: faq.user_vote === 'helpful' ? '2px solid' : '1px solid transparent',
              borderColor: faq.user_vote === 'helpful' ? 'success.dark' : 'transparent',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: faq.user_vote === 'helpful'
                  ? 'success.dark'
                  : 'action.hover',
                transform: 'scale(1.1)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              '& .MuiSvgIcon-root': {
                color: faq.user_vote === 'helpful' ? 'common.white' : 'inherit',
                transition: 'color 0.2s ease-in-out',
              },
            }}
          >
            <ThumbUpIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: '20px', textAlign: 'center' }}
          >
            {faq.helpful_count || 0}
          </Typography>

          <IconButton
            size="small"
            onClick={handleThumbsDown}
            aria-label={t('faq.actions.not_helpful')}
            aria-pressed={faq.user_vote === 'not_helpful'}
            sx={{
              color: faq.user_vote === 'not_helpful' ? 'error.main' : 'action.active',
              bgcolor: faq.user_vote === 'not_helpful' ? 'error.main' : 'transparent',
              border: faq.user_vote === 'not_helpful' ? '2px solid' : '1px solid transparent',
              borderColor: faq.user_vote === 'not_helpful' ? 'error.dark' : 'transparent',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: faq.user_vote === 'not_helpful'
                  ? 'error.dark'
                  : 'action.hover',
                transform: 'scale(1.1)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              '& .MuiSvgIcon-root': {
                color: faq.user_vote === 'not_helpful' ? 'common.white' : 'inherit',
                transition: 'color 0.2s ease-in-out',
              },
            }}
          >
            <ThumbDownIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: '20px', textAlign: 'center' }}
          >
            {faq.not_helpful_count || 0}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Approval Actions */}
          <ApprovalActions
            faq={faq}
            canApprove={isModerator}
            canReject={isModerator}
            canWithdraw={Boolean(user && faq.owner_id === user.id)}
            canDelete={isModerator}
            variant="menu"
            size="small"
            onSuccess={showSuccess}
            onError={showError}
            onDelete={onDeleteFAQ}
          />

          {/* View Details Button */}
          <Button
            size="small"
            variant="outlined"
            onClick={handleViewDetails}
          >
            {t('faq.list.details')}
          </Button>

          {/* Edit Button - Only for non-approved FAQs or moderators */}
          {(faq.approval_status !== ApprovalStatus.Approved || isModerator) && (
            <Tooltip title={
              faq.approval_status === ApprovalStatus.Approved && isModerator
                ? t('faq.actions.edit_approved_warning', 'Editing will reset approval status')
                : t('faq.actions.edit')
            }>
              <IconButton size="small" onClick={handleEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardActions>
    </Card>
  );
});

FAQCard.displayName = 'FAQCard';

/**
 * FAQList - Display grid of FAQ cards
 *
 * Performance: Wrapped in React.memo to prevent unnecessary re-renders
 * Each FAQ card is also memoized individually
 *
 * @example
 * ```tsx
 * <FAQList
 *   faqs={filteredFaqs}
 *   isLoading={isLoading}
 *   isModerator={isModerator}
 *   user={user}
 *   onRateFAQ={handleRateFAQ}
 *   onViewDetails={handleViewDetails}
 *   onEditFAQ={handleEditFAQ}
 *   getVisibilityIcon={getVisibilityIcon}
 *   getVisibilityText={getVisibilityText}
 *   showSuccess={showSuccess}
 *   showError={showError}
 * />
 * ```
 */
export const FAQList = React.memo<FAQListProps>(({
  faqs,
  isLoading,
  isModerator,
  user,
  onRateFAQ,
  onUnrateFAQ,
  onViewDetails,
  onEditFAQ,
  onDeleteFAQ,
  getVisibilityIcon,
  getVisibilityText,
  showSuccess,
  showError
}) => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={3}>
      {isLoading ? (
        // Loading skeletons
        Array.from({ length: 6 }).map((_, index) => (
          <Grid size={{ xs: 12, md: 6 }} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Skeleton variant="text" width="80%" height={32} />
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="60%" height={20} />
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Skeleton variant="rectangular" width={60} height={24} />
                  <Skeleton variant="rectangular" width={80} height={24} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))
      ) : faqs.length === 0 ? (
        // Empty state
        <Grid size={12}>
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <HelpIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t('faq.list.noFaqsFound')}
            </Typography>
          </Paper>
        </Grid>
      ) : (
        // FAQ cards
        faqs.map((faq) => (
          <Grid size={{ xs: 12, md: 6 }} key={faq.document_id}>
            <FAQCard
              faq={faq}
              isModerator={isModerator}
              user={user}
              onRateFAQ={onRateFAQ}
              onUnrateFAQ={onUnrateFAQ}
              onViewDetails={onViewDetails}
              onEditFAQ={onEditFAQ}
              onDeleteFAQ={onDeleteFAQ}
              getVisibilityIcon={getVisibilityIcon}
              getVisibilityText={getVisibilityText}
              showSuccess={showSuccess}
              showError={showError}
            />
          </Grid>
        ))
      )}
    </Grid>
  );
});

FAQList.displayName = 'FAQList';
